export type SortableValue = string | number | Date | null | undefined;
export type NullsHandling = 'equal' | 'first' | 'last';

export interface SortState<K extends string> {
  key: K;
  ascending: boolean;
}

export type SortAccessorMap<T, K extends string> = Record<K, (item: T) => SortableValue>;

const normalizeValue = (value: SortableValue): string | number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  return value;
};

export const compareValues = (
  valueA: SortableValue,
  valueB: SortableValue,
  ascending: boolean,
  nullsHandling: NullsHandling = 'equal',
): number => {
  const normalizedA = normalizeValue(valueA);
  const normalizedB = normalizeValue(valueB);

  if (normalizedA === null || normalizedB === null) {
    if (nullsHandling === 'equal') {
      return 0;
    }
    if (normalizedA === null && normalizedB === null) {
      return 0;
    }
    if (normalizedA === null) {
      return nullsHandling === 'first' ? -1 : 1;
    }
    return nullsHandling === 'first' ? 1 : -1;
  }

  let result = 0;
  if (typeof normalizedA === 'number' && typeof normalizedB === 'number') {
    result = normalizedA === normalizedB ? 0 : normalizedA < normalizedB ? -1 : 1;
  } else {
    const stringA = String(normalizedA).toLowerCase();
    const stringB = String(normalizedB).toLowerCase();
    result = stringA === stringB ? 0 : stringA < stringB ? -1 : 1;
  }

  return ascending ? result : -result;
};

export const sortItems = <T, K extends string>(
  items: T[],
  key: K,
  ascending: boolean,
  accessors: SortAccessorMap<T, K>,
  nullsHandling: NullsHandling = 'equal',
): T[] => {
  const accessor = accessors[key];
  if (!accessor) {
    return items;
  }

  return [...items].sort((itemA, itemB) =>
    compareValues(accessor(itemA), accessor(itemB), ascending, nullsHandling),
  );
};
